import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { STATUS } from '../models/status';
import { HttpService } from './http.service';
import { UserService } from './users.service';

@Injectable()
export class TransactionsService {
  constructor(
    private http: HttpService,
    private userService: UserService,
  ) {}

  getTransactions(
    page: number = 1,
    page_size: number | null = 10,
    status: any,
    id: number | null = null,
    wallet_id: string | null = null,
    provider_id: number | null = null,
    user_id: number | null = null,
    id_number: number | null = null,
    transaction_type: string | null = null,
    start_date: string | null = null,
    end_date: string | null = null,
    type: string | null,
    currency: string | null = null,
    timezone: string | null = null,
  ) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', page_size!.toString());

    // Append parameters only if they are not null and not empty array
    if (id !== null) params = params.set('id', id.toString());
    if (wallet_id !== null) params = params.set('wallet_id', wallet_id);
    if (provider_id !== null)
      params = params.set('provider_id', provider_id.toString());
    if (user_id !== null) params = params.set('user_id', user_id.toString());
    if (id_number !== null)
      params = params.set('id_number', id_number.toString());
    if (transaction_type !== null)
      params = params.set('transaction_type', transaction_type);
    if (status !== null) params = params.set('status', status);
    if (start_date !== null) params = params.set('start_date', start_date);
    if (end_date !== null) params = params.set('end_date', end_date);
    if (type !== null) params = params.set('type', type);
    if (currency !== null) params = params.set('currency', currency);
    if (timezone !== null) params = params.set('timezone', timezone);

    // Construct the URL with conditional parameters
    const baseUrl = 'transactions'; // Adjust the base URL as needed
    return this.http.get(baseUrl, params);
  }

  getCashouts(
    page: number = 1,
    page_size: number = 10,
    status: any,
    id: number | null = null,
    sender_wallet_id: string | null = null,
    provider_id: number | null = null,
    client_id: number | null = null,
    id_number: number | null = null,
    transaction_type: string | null = null,
    start_date: string | null = null,
    end_date: string | null = null,
    type: string | null = null,
    currency: string | null = null,
    timezone: string | null = null,
  ) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', page_size.toString());

    // Append parameters only if they are not null and not empty array
    if (id !== null) params = params.set('id', id.toString());
    if (sender_wallet_id !== null)
      params = params.set('sender_wallet_id', sender_wallet_id);
    if (
      provider_id !== null &&
      (!Array.isArray(provider_id) || provider_id.length > 0)
    )
      params = params.set(
        'provider_id',
        Array.isArray(provider_id)
          ? provider_id.join(',')
          : provider_id.toString(),
      );
    if (
      client_id !== null &&
      (!Array.isArray(client_id) || client_id.length > 0)
    )
      params = params.set(
        'client_id',
        Array.isArray(client_id) ? client_id.join(',') : client_id.toString(),
      );
    if (id_number !== null)
      params = params.set('id_number', id_number.toString());
    if (transaction_type !== null)
      params = params.set('transaction_type', transaction_type);
    if (status !== null) params = params.set('status', status);
    if (start_date !== null) params = params.set('start_date', start_date);
    if (end_date !== null) params = params.set('end_date', end_date);
    if (type !== null) params = params.set('type', type);
    if (currency !== null) params = params.set('currency', currency);
    if (timezone !== null) params = params.set('timezone', timezone);

    // Construct the URL with conditional parameters
    const baseUrl = 'cashouts'; // Adjust the base URL as needed
    return this.http.get(baseUrl, params);
  }

  getCashins(
    page: number = 1,
    page_size: number = 10,
    status: any,
    provider_service_id: number | null = null,
    client_id: number | null = null,
    start_date: string | null = null,
    end_date: string | null = null,
    currency: string | null = null,
  ) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', page_size.toString());

    // Append parameters only if they are not null
    if (provider_service_id !== null)
      params = params.set(
        'provider_service_id',
        provider_service_id.toString(),
      );
    if (client_id !== null)
      params = params.set('client_id', client_id.toString());
    if (status !== null) params = params.set('status', status);
    if (start_date !== null) params = params.set('start_date', start_date);
    if (end_date !== null) params = params.set('end_date', end_date);
    if (currency !== null) params = params.set('currency', currency);

    // Construct the URL with conditional parameters
    const baseUrl = 'cashins'; // Adjust the base URL as needed
    return this.http.get(baseUrl, params);
  }

  getTransactionsCount(
    client_id: number | null = null,
    start_date: string | null = null,
    end_date: string | null = null,
  ) {
    let params = new HttpParams();
    params = params.set('type', 'cashout');

    if (client_id !== null)
      params = params.set('client_id', client_id.toString());
    if (start_date !== null)
      params = params.set('start_date', start_date.toString());
    if (end_date !== null) params = params.set('end_date', end_date.toString());
    return this.http.get(`cashouts/status-counts`, params);
  }

  getVESummariesByStatus() {
    return this.http.get(`summary/by-status`);
  }

  processCashouts() {
    return this.http.get(`cashouts/process/queue`);
  }

  updateTransaction(body: any) {
    return this.http.patch(`cashout/` + body.id, body);
  }

  clearTransactionErrors(transactionId: number | string) {
    return this.http.delete(`cashout/${transactionId}/errors`);
  }

  addTransaction(body: any) {
    return this.http.post(`cashouts`, body);
  }

  addCashin(body: any) {
    return this.http.post(`cashin`, body);
  }

  r4ImmediateDebitOtp(body: {
    provider_service_id: number | string;
    client_id: number | string;
    destiny_wallet_id: string;
    amount: number | string;
    banco: string;
    telefono: string;
    cedula?: string;
    id_type?: string;
    id_number?: string;
    nombre: string;
    concepto: string;
  }) {
    return this.http.post('r4/cashin/immediate-debit/otp', body);
  }

  r4ImmediateDebitConfirm(body: { cashin_id: number | string; otp: string }) {
    return this.http.post('r4/cashin/immediate-debit/confirm', body);
  }

  r4ImmediateCredit(body: any) {
    return this.http.post('r4/cashout/immediate-credit', body);
  }

  createPayinLink(body: {
    amount: number;
    provider_service_id: number;
    client_id: number;
    currency?: string;
    destiny_wallet_id?: string | number;
    final_url?: string;
    redirect_url?: string;
    payer_name?: string;
    payer_email?: string;
    payer_phone?: string;
    purpose?: string;
    payment?: { method?: string; bank?: string };
    third_party?: Record<string, unknown>;
    recipient?: Record<string, unknown>;
  }) {
    return this.http.post('cashin/create-payin-link', body);
  }

  /** Sincroniza cashins Bitso funding (retorno PSE o cron del cliente). */
  pollBitsoFunding(payinId?: string) {
    let params = new HttpParams();
    if (payinId) {
      params = params.set('payin_id', payinId);
    }
    return this.http.get('cashins/poll/bitso-funding', params);
  }

  /** Instrucciones IBAN Depasify para cashin inbound manual. */
  getDepaDepositInstructions(params: {
    currency: string;
    client_id?: number;
  }) {
    const query = new URLSearchParams();
    query.set('currency', params.currency);
    if (params.client_id != null) {
      query.set('client_id', String(params.client_id));
    }
    return this.http.get(`account/fiat/deposit-instructions?${query.toString()}`);
  }

  /** (Backoffice) Simula pago Minteo (sandbox) para un payin_id. */
  simulateMinteoPayment(
    payinId: string,
    body: {
      amount: string | number;
      token?: string;
      reference?: string;
      status?: string;
    },
  ) {
    return this.http.post(`cashin/minteo/payment-simulator/${payinId}`, body);
  }

  /** (Backoffice) Simula inbound payment Fuse/Lorum (sandbox) vía external-providers. */
  simulateFuseInboundPayment(body: {
    payload: Record<string, unknown>;
    persist_cashin?: boolean;
    client_id?: number;
    destiny_wallet_id?: string | number;
    currency?: string;
    provider_service_id?: number;
  }) {
    return this.http.post('cashin/fuse/inbound-simulator', body);
  }

  rejectCashin(transactionId: number) {
    return this.http.patch(`cashin/${transactionId}/reject`, {});
  }

  approveCashin(transactionId: number) {
    return this.http.patch(`cashin/${transactionId}/approve`, {});
  }

  downloadCashoutVoucher(transactionId: number) {
    return this.http.getBlob(`transactions/${transactionId}/voucher`);
  }

  editCashinMatch(
    transactionId: number,
    body: {
      amount: string | number;
      reference?: string;
      origin_bank?: string;
      commission?: number;
    },
  ) {
    return this.http.patch(`cashin/${transactionId}/match`, body);
  }

  uploadCSV(body: any) {
    return this.http.post(`cashouts/upload/csv`, body, true);
  }

  uploadCashinCSV(body: any) {
    return this.http.post(`cashins/upload/csv`, body, true);
  }
}
